import React, { useState } from 'react';
import { logger } from '../services/logger';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';

interface Option {
  value: string;
  label: string;
}

// Custom Select component that uses our design system
const Select: React.FC<{
  label: string;
  value: string;
  options: Option[];
  onChange: (value: string) => void;
}> = ({ label, value, options, onChange }) => {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-text-secondary mb-1">{label}</label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 bg-surface-primary dark:bg-surface-secondary border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-30 focus:border-primary transition-all duration-200 pr-10 appearance-none"
        >
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-text-tertiary">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
    </div>
  );
};

const AnalysisPage: React.FC = () => {
  const [emailContent, setEmailContent] = useState('');
  const [analysisType, setAnalysisType] = useState('standard');
  const [isLoading, setIsLoading] = useState(false);
  
  const analysisOptions: Option[] = [
    { value: 'standard', label: 'Standard Analysis' },
    { value: 'detailed', label: 'Detailed Analysis' },
    { value: 'quick', label: 'Quick Scan' }
  ];
  
  const handleAnalyze = () => {
    if (!emailContent.trim()) {
      alert('Please enter email content to analyze');
      return;
    }
    
    setIsLoading(true);
    logger.info(`Starting ${analysisType} analysis`);
    
    // Simulate analysis process
    setTimeout(() => {
      setIsLoading(false);
      logger.info('Analysis complete');
    }, 2000);
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="text-center mb-10 animate-fade-in">
        <h1 className="text-4xl font-display font-bold mb-4 text-transparent bg-clip-text bg-gradient-primary">
          Email Analysis
        </h1>
        
        <p className="text-xl text-text-secondary animate-fade-in" style={{ animationDelay: '200ms' }}>
          Paste your email content below and select the type of analysis to perform.
        </p>
      </div>
      
      <Card 
        variant="glass" 
        className="mb-8 animate-fade-in" 
        style={{ animationDelay: '400ms' }}
      >
        <div className="mb-4">
          <label className="block text-sm font-medium text-text-secondary mb-1">
            Email Content
          </label>
          <textarea
            value={emailContent}
            onChange={(e) => setEmailContent(e.target.value)}
            rows={10}
            placeholder="Paste email content here..."
            className="w-full px-3 py-2 bg-surface-primary dark:bg-surface-secondary border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-30 focus:border-primary transition-all duration-200"
          />
        </div>
        
        <Select
          label="Analysis Type"
          value={analysisType}
          options={analysisOptions}
          onChange={setAnalysisType}
        />
        
        <div className="flex justify-end">
          <Button
            variant="primary"
            hasRipple
            onClick={handleAnalyze}
            isLoading={isLoading}
          >
            {isLoading ? 'Analyzing...' : 'Analyze Email'}
          </Button>
        </div>
      </Card>
      
      {!isLoading && emailContent && (
        <Card 
          variant="glass" 
          className="animate-fade-in transform hover:scale-102 transition-transform duration-300" 
          style={{ animationDelay: '600ms' }}
        >
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-secondary flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-display font-semibold">Analysis Results</h2>
              <p className="text-text-secondary text-sm">
                Preliminary content review
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <div className="p-4 bg-surface-secondary bg-opacity-30 rounded-lg">
              <h3 className="text-md font-medium mb-2">Email Source</h3>
              <div className="h-24 flex items-center justify-center bg-surface-primary dark:bg-surface-secondary rounded-lg border border-divider p-4">
                <p className="text-text-tertiary">Source information will appear here</p>
              </div>
            </div>
            <div className="p-4 bg-surface-secondary bg-opacity-30 rounded-lg">
              <h3 className="text-md font-medium mb-2">Threat Analysis</h3>
              <div className="h-24 flex items-center justify-center bg-surface-primary dark:bg-surface-secondary rounded-lg border border-divider p-4">
                <p className="text-text-tertiary">Threat information will appear here</p>
              </div>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end">
            <Button
              variant="outline"
              size="sm"
              className="mr-2"
            >
              Save Results
            </Button>
            <Button
              variant="glass"
              size="sm"
            >
              View Details
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AnalysisPage; 